import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Autocomplete, Checkbox, TextField } from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import usePermissions from "../../hooks/usePermissions";
import UserAPIs from "../../apiRoutes/User";
import AdsAPIs from "../../apiRoutes/Ads";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import { GET_USERS_QUERY_KEY, GET_AD_REQUESTS_QUERY_KEY } from "../../utils/constants/queryKeys";

const userLabel = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(" ")
  || u.email
  || u.username
  || (u.id ? `User ${u.id}` : "");

/**
 * Autocomplete typeahead to filter a list by the person who requested the ad.
 * `value` is the selected user id as a string ("" = none); `onChange` receives the id string.
 *
 * The option source is chosen by permission, so the same component works for every role:
 * - Users with user.manage / user.list → the full user directory (users API), scoped to
 *   `agencyId` when provided.
 * - Everyone else → requesters derived from the ad-requests list (every role has
 *   ad_request.view), so no user-list permission is required. This covers requesters
 *   present in the most recent ~200 requests, not the entire user directory.
 */
const RequestedByFilter = ({ value, onChange, agencyId, placeholder = "Requested by", fullWidth = false, disablePortal = false, multiple = false }) => {
  const axiosPrivate = useAxiosPrivate();
  const { hasPermission } = usePermissions();
  const canListUsers =
    hasPermission(PERMISSION_CODES.USER_MANAGE) || hasPermission(PERMISSION_CODES.USER_LIST);

  const { data: usersResult } = useQuery({
    queryKey: [...GET_USERS_QUERY_KEY, agencyId ?? null],
    queryFn: () =>
      UserAPIs.list({
        axiosPrivate,
        status: "approved",
        limit: 200,
        offset: 0,
        ...(agencyId != null ? { agency_id: agencyId } : {}),
      }),
    enabled: canListUsers,
  });

  const { data: requesterSource } = useQuery({
    queryKey: [...GET_AD_REQUESTS_QUERY_KEY, "requester-options", agencyId ?? null],
    queryFn: () =>
      AdsAPIs.listAdRequests({
        axiosPrivate,
        requested_for_agency_id: agencyId,
        limit: 200,
        offset: 0,
      }),
    enabled: !canListUsers,
  });

  const options = useMemo(() => {
    if (canListUsers) {
      return [...(usersResult?.data ?? [])].sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
    }
    const map = new Map();
    for (const r of requesterSource?.data ?? []) {
      const id = r.requested_by_id;
      if (id == null || map.has(id)) continue;
      map.set(id, r.requested_by_name || `User ${id}`);
    }
    return Array.from(map, ([id, name]) => ({ id, first_name: name }));
  }, [canListUsers, usersResult, requesterSource]);

  const selectedIds = multiple ? String(value || "").split(",").filter(Boolean) : [];

  return (
    <Autocomplete
      multiple={multiple}
      disableCloseOnSelect={multiple}
      value={
        multiple
          ? options.filter((u) => selectedIds.includes(String(u.id)))
          : options.find((u) => String(u.id) === value) || null
      }
      options={options}
      getOptionLabel={userLabel}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      onChange={(_e, v) =>
        multiple
          ? onChange(v.map((u) => String(u.id)).join(","), v.map(userLabel))
          : onChange(v ? String(v.id) : "", v ? userLabel(v) : "")
      }
      renderOption={
        multiple
          ? (props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox size="small" checked={selected} sx={{ p: 0.5, mr: 1 }} />
                <span className="min-w-0 leading-tight">
                  <span className="block truncate text-[13px] text-gray-800">{userLabel(option)}</span>
                  {option.email && userLabel(option) !== option.email && (
                    <span className="block truncate text-[11px] text-gray-400">{option.email}</span>
                  )}
                </span>
              </li>
            )
          : undefined
      }
      renderTags={
        multiple
          ? (selected) => <span className="pl-1 text-sm text-gray-800">{selected.length} selected</span>
          : undefined
      }
      disablePortal={disablePortal}
      ListboxProps={multiple ? { sx: { "& .MuiAutocomplete-option": { py: 0.5, minHeight: "auto" } } } : undefined}
      sx={{
        width: fullWidth ? "100%" : 200,
        "& .MuiOutlinedInput-root": {
          ...(multiple ? { minHeight: 36, flexWrap: "wrap", gap: "2px", py: "2px !important" } : { height: 36 }),
          padding: "0 8px !important",
          fontSize: "14px",
          backgroundColor: "#ffffff",
          borderRadius: "6px",
          boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
          color: "#111827",
        },
        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
          borderColor: "#d1d5db",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#d1d5db",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
        "& .MuiAutocomplete-input": {
          padding: "0 4px !important",
          fontSize: "14px",
        },
        "& .MuiAutocomplete-input::placeholder": {
          color: "#6b7280",
          opacity: 1,
        },
      }}
      renderInput={(params) => <TextField {...params} placeholder={placeholder} />}
    />
  );
};

export default RequestedByFilter;
